/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { compose } from 'recompose';
import loadable from '@loadable/component';
import {
    object, string, bool, shape, number, array,
} from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { withRouter } from 'react-router';
import { useQuery, withApollo } from 'react-apollo';
import ReactHtmlParser from 'react-html-parser';
import track from 'react-tracking';

import mbpUtil from 'mbp-api-util';
import mbpLogger from 'mbp-logger';

import * as app from '../../../../../state/ducks/App';
import { getAddressSource } from '../../../../../state/ducks/App/App-Selectors';
import DesktopMobile from '../../../../helpers/DesktopMobile/DesktopMobile';
import { getFeatureFlags } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { trackPage, trackProductData } from '../../../../helpers/tracking/common/commonTrackingHelpers';
import { getLanguageName } from '../../../../../state/ducks/Member/ducks/Auth/helper/helper';
import MiniCart from '../../../../pages/Checkout/containers/CheckoutFood/components/Partials/MiniCart/MiniCart';
import SiteLoader from '../../../../pages/Checkout/containers/CheckoutFood/components/Partials/SiteLoader/SiteLoader';
import { addLibraries } from '../../../../helpers/commonFunctions';

// UI Components
import GraphqlProductSeoSchema from '../Partials/GraphqlProductSeoSchema/GraphqlProductSeoSchema';
import { dateStringReformat, getBreadcrumbArray, addOgDynamicData } from '../Partials/common/commonProductPageHelper';
import GraphqlProductImageUI from '../Partials/GraphqlProductImageUI';
import GraphqlProductImageUIMobile from '../Partials/ProductImageUI/ProductImageUIMobile';
import GraphqlPlantsPDPDataWrapper from './Partials/GraphqlPlantsPDPDataWrapper';
import PDPDeliveryPopupContainer from '../Partials/GraphqlPDPDeliveryPopup/PDPDeliveryPopupContainer';
import GraphqlProductDetailsTabs from '../Partials/GraphqlProductDetailsTabs/GraphqlProductDetailsTabs';
import ShippingInformation from './Partials/ShippingInformation';
import pdpDictionary from '../translations/pdpTranslations.json';
import {
    handleSkuChange, triggerSubscriptionCheck, handlePersonalizeInputChange, getUpdatedPersonalizedProdData, handleMobileAgeVerification, renderVase, plantsCareData,
} from './ProductPageFunctions';
import MobileHeader from './Partials/MobileHeader';

// Data
import CommonStates from './Partials/CommonStates';
import ErrorMessage from './Partials/ErrorMessage';
// import vaseList from './Partials/vaseList.json';
import { getLoaderStatus } from '../../../../../state/ducks/Checkout/ducks/Common/Common-Selectors';
import findCategoryPageByUrl from '../../../../gql/queries/findCategoryPageByURL';
import ShopNotAvailableModal from '../../../../pages/Checkout/containers/Common/ModalWrapper/Partials/ShopNotAvailableModal';
import ShippingDisclaimer from './Partials/ShippingDisclaimer';

const appConfigSelectors = app.ducks.config.selectors;
const CustomizedSnackbar = loadable(() => import(/* webpackChunkName: "CustomizedSnackbar" */ './Partials/CustomizedSnackbar'));
const ProductTitle = loadable(() => import(/* webpackChunkName: "ProductTitle" */ '../Partials/ProductTitle'));
const ProductDivider = loadable(() => import(/* webpackChunkName: "ProductDivider" */ '../Partials/ProductDivider'));
const ProductRecentlyViewed = loadable(() => import(/* webpackChunkName: "ProductRecentlyViewed" */ '../../GraphqlCommonComponents/ProductRecentlyViewed'));
const ProductCarouselBuilder = loadable(() => import(/* webpackChunkName: "ProductCarouselBuilder" */ './Partials/RecommendationsBuilder/Partials/ProductCarouselBuilder'));
const ProductPartNumber = loadable(() => import(/* webpackChunkName: "ProductPartNumber" */ './Partials/ProductPartNumber'));
const ProductBlogSection = loadable(() => import(/* webpackChunkName: "ProductBlogSection" */ '../Partials/ProductBlogSection'));
const ProductOurStory = loadable(() => import(/* webpackChunkName: "ProductOurStory" */ '../Partials/ProductOurStory'));
const RecommendationsBuilder = loadable(() => import(/* webpackChunkName: "RecommendationsBuilder" */ './Partials/RecommendationsBuilder/RecommendationsBuilder'));

const styles = (theme) => ({

    foodRoot: {
        width: '98%',
        margin: 'auto',
    },
    flowerRoot: {
        maxWidth: '1270px',
        margin: 'auto',
        paddingTop: 15,
        [theme.breakpoints.down('md')]: {
            paddingTop: 0,
        },
    },
    contentTop: {
        // Desktop
        margin: '0px auto',
        maxWidth: '1380px',
        color: theme.palette.black,
        // Tablet
        [theme.breakpoints.down('md')]: {
            margin: '20px 20px 30px',
        },
        // Mobile
        [theme.breakpoints.up(768)]: {
            display: 'flex',
        },
        [theme.breakpoints.down('sm')]: {
            margin: '10px',
        },
    },
    contentLeft: {
        width: '100%',
        // Tablet
        [theme.breakpoints.down('md')]: {
            flex: '1 1 auto',
            display: 'flex',
            maxWidth: '40%',
            marginRight: '20px',
            flexDirection: 'column',
        },
        // Mobile
        [theme.breakpoints.down(768)]: {
            maxWidth: '100%',
        },
        marginRight: '24px',
        maxWidth: '665px',
    },
    contentRight: {
        width: '100%',
        maxWidth: '980px',
        // maxWidth: '50%',
        [theme.breakpoints.down('md')]: {
            paddingTop: '20px',
        },
        '&>div': {
            marginBottom: 0,
            padding: '0px 5px',
            [theme.breakpoints.down('md')]: {
                padding: 0,
            },
            '&>div': {
                float: 'none',
                margin: 0,
                padding: 0,
            },
        },
        // Tablet
        [theme.breakpoints.down('md')]: {
            width: 'calc(60% - 20px)',
            display: 'flex',
            flexDirection: 'column',
        },
        // Mobile
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
        // Mobile
        [theme.breakpoints.down(768)]: {
            marginTop: '25px',
        },
    },
    vaseTemplateWrapper: {
        maxWidth: '50%',
        [theme.breakpoints.down('md')]: {
            maxWidth: '980px',
        },
    },
    contentBottom: {
        width: '100%',
        margin: '40px auto 60px auto',
        maxWidth: '1380px',
        // Tablet
        [theme.breakpoints.down('md')]: {
            padding: '0 20px 0',
        },
        // Mobile
        [theme.breakpoints.down('sm')]: {
            margin: '0 0 15px 0',
            padding: '0',
        },
    },
    breadCrumbBody: {
        // display: 'none',
        margin: '30px auto 10px auto',
        maxWidth: 1380,
        [theme.breakpoints.down('md')]: {
            padding: '0 20px 0',
        },
        // Desktop & Tablet
        '&--desktop': {
            [theme.breakpoints.up('sm')]: {
                display: 'flex',
            },
        },
        // Mobile
        '&--mobile': {
            [theme.breakpoints.down('sm')]: {
                display: 'flex',
                justifyContent: 'center',
            },
        },
        '&--mobile.club-mobile-pdp': {
            [theme.breakpoints.down('sm')]: {
                display: 'block',
                textAlign: 'left',
                padding: '0 10px 0',
                '& li': {
                    display: 'inline-block',
                },
                '& ol': {
                    textAlign: 'left',
                },
            },
        },
    },
    productName: {
        width: '100%',
        marginTop: 0,
        fontSize: '2.5rem',
        fontFamily: theme.typography.secondaryFontFamily,
        lineHeight: '40px',
        marginBottom: 0,
        [theme.breakpoints.down('md')]: {
            marginBottom: '10px',
        },
        // Mobile
        [theme.breakpoints.down(768)]: {
            display: 'none',
        },
    },
    productRatingContainer: {
        width: '370px',
    },
    saLogo: {
        height: '43px',
        marginLeft: '-25px',
    },
    reviewCountContainer: {
        display: 'flex',
        alignItems: 'center',
        height: '10px',
    },
    ratingStars: {
        display: 'inline-block',
        margin: '0 17px 0 0',
        height: '19px',
        '& svg': {
            width: '20px',
            height: 'auto',
            color: '#E4984B',
        },
    },
    reviewCount: {
        display: 'inline-block',
        color: '#B6B6B6',
        fontSize: '15px',
        lineHeight: '14px',
    },
    contentBottomDivider: {
        margin: '40px 0 15px 0',
        textAlign: 'center',
        width: '100%',
        '&--title': {
            margin: '0',
            textAlign: 'center',
            fontSize: '16px',
            fontWeight: '600',
        },
    },
    passportEligibleImage: {
        width: '24%',
        [theme.breakpoints.down('md')]: {
            width: '45%',
            marginBottom: '10px',
        },
        [theme.breakpoints.down(768)]: {
            width: '38%',
            margin: '0 auto 5px',
        },
    },
    heading: {
        fontSize: '20px',
        textTransform: 'capitalize',
        fontWeight: '500',
        textAlign: 'center',
    },

    descriptionBlock: {
        height: '100%',
        display: 'inline-block',
        padding: '0 15px',
        fontSize: '15px',
    },
    subHeading: {
        display: 'flex',
        marginTop: '15px',
        '& h2': {
            fontSize: '16px',
            margin: '0',
        },
        '& span': {
            margin: '0 0 0 5px',
        },
    },
    longDescription: {
        display: 'block',
        [theme.breakpoints.down('sm')]: {
            display: 'none',
            '&.plaLongDescription': {
                display: 'block',
            },
        },
    },
    longDescriptionMobile: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
            '&.plaShowToggle': {
                display: 'none',
            },
        },
    },
    hidden: {
        display: 'none',
    },
    toggleDescription: {
        margin: '0 0 20px 0',
        padding: '0',
        color: '#5e3987',
        fontSize: '14px',
        fontWeight: '700',
        textDecoration: 'underline',
        border: 'none',
        cursor: 'pointer',
        background: 'none',
    },
    partNumberMobileStyle: {
        fontSize: '.8125rem',
        fontWeight: '500',
        margin: 0,
        marginLeft: 15,
    },
    customerReviews: {
        '& .yotpo .yotpo-pager .yotpo-page-element.yotpo-active': {
            color: '#fff !important',
            padding: '3px 8px',
            borderRadius: '50%',
            backgroundColor: `${theme.palette.primary.main} !important`,
        },
    },
    prReviewDisplay: {
        '& .p-w-r .pr-review-snapshot .pr-review-snapshot-simple .pr-review-snapshot-snippets-headline': {
            marginRight: '10px',
        },
        '& .pr-snippet': {
            display: 'flex',
            minWidth: 'fit-content',
        },
        '& .p-w-r .modal__body .pr-read-review-flagging-in .pr-rd-helpful-action-btn-group': {
            display: 'flex',
        },
        '& .pr-rd-helpful-action-group': {
            display: 'flex',
        },
        [theme?.breakpoints?.down('980')]: {
            '& .p-w-r .pr-review-snapshot .pr-snippet .pr-snippet-stars-png .pr-star-v4': {
                height: '15px',
                width: '15px',
            },
            '& .p-w-r .pr-review-snapshot.pr-snapshot-mobile .pr-review-snapshot-simple .pr-snippet-rating-decimal': {
                display: 'none',
            },
            '& .pr-review-snapshot-snippets': {
                justifyContent: 'left',
            },
            '& .p-w-r .pr-review-display .pr-rd-helpful-action .pr-rd-helpful-text': {
                display: 'block',
            },
            '& .pr-rd-flag-review-container': {
                display: 'block',
            },
        },
        [theme?.breakpoints?.down('sm')]: {
            '& .pr-snippet': {
                flexDirection: 'column',
                marginTop: '-20px',
            },
        },
        [theme?.breakpoints?.down('xs')]: {
            '& .p-w-r .pr-review-display .pr-rd-helpful-action .pr-rd-helpful-text': {
                display: 'none',
            },
            '& .pr-rd-flag-review-container': {
                display: 'none',
            },
            '& .pr-snippet': {
                flexDirection: 'column',
                marginTop: '0px',
            },
        },
    },
    hidePRStarDistribution: {
        '& .p-w-r .pr-review-snapshot.pr-review-enhanced .pr-review-snapshot-block-container .pr-review-snapshot-block:last-child': {
            display: 'none',
        },
    },
    prQuestionDisplay: {
        '& .p-w-r .pr-waqwaa': {
            maxWidth: '100%',
        },
        '& .p-w-r .pr-qa-display .pr-btn-ask-question .pr-cross-icon': {
            display: 'none',
        },
    },
});

const GraphqlPlantsProductPageBuilder = ({
    classes, product, location, content, presentation_family, productBaseContent, brandId, isPassportBundleItem, isPassportBundleProduct, cartError, seoData, history, plaSku, restrictedStates,
}) => {
    const isSmallScreen = !useMediaQuery('(min-width:767px)');
    const productSkus = product ? product.productSkus : null;
    const brand = useSelector(appConfigSelectors.getBrand);
    const showLoadingMessage = useSelector(getLoaderStatus);
    const isVaseTemplate = productBaseContent?.entries?.[0]?.additional_information?.[0]?.template_selector?.reference?.[0]?.title === 'Vase selector';
    const selectors = productBaseContent?.entries?.[0]?.additional_information?.[0]?.template_selector?.reference?.[0]?.selector;
    const vaseList = [];
    if (selectors) {
        selectors.map((selector) => {
            vaseList.push({ vase_image: selector.image.url, code: selector.template_code });
        });
    }
    const sku_ranking = productBaseContent?.entries?.[0]?.sku_section?.sku_ranking || content?.entries?.[0]?.sku_section?.sku_ranking;
    const commonStates = CommonStates(product, brandId, plaSku);
    const {
        sortedData, setSortedData, selectedSku, setSelectedSku, quantity, setQuantity, hasWine, setHasWine,
        hasAddons, setHasAddons, isAddToCartEnabled, setIsAddToCartEnabled, disableAddToCart, setDisableAddToCart,
        personalizedData, setPersonalizedData, minicartModalOpen, setMinicartModalOpen, mobileAgeVerificationOpen,
        setMobileAgeVerificationOpen, baseProduct, setBaseProduct, addToCartData, setAddToCartData, subscription,
        setSubscription, subscriptionDetails, setSubscriptionDetails, selectedState, setSelectedState, alertType,
        setAltertType, addToCartClicked, setAddToCartClicked, deliveryDate, setDeliveryDate, variantVaseList,
        toastError, setToastError,
    } = commonStates;

    const skusHaveDuplicateRanksOrAreUnranked = (skus) => {
        const ranks = new Set();
        return skus.some((sku) => {
            if (sku.rank === 99) {
                return true;
            }
            return ranks.size === ranks.add(sku.rank).size;
        });
    };

    useEffect(() => {
        if (productSkus) {
            switch (sku_ranking) {
                case 'rank':
                    if (!skusHaveDuplicateRanksOrAreUnranked(productSkus)) {
                        setSortedData(productSkus.slice(0).sort((a, b) => Number(a.rank) - Number(b.rank)));
                    }
                    break;
                case 'low to high':
                    setSortedData(productSkus.slice(0).sort((a, b) => Number(a.prices?.[0]?.value) - Number(b.prices?.[0]?.value)));
                    break;
                case 'high to low':
                    setSortedData(productSkus.slice(0).sort((a, b) => Number(b.prices?.[0]?.value) - Number(a.prices?.[0]?.value)));
                    break;
                case 'next available date':
                    setSortedData(productSkus.slice(0).sort((a, b) => new Date(dateStringReformat(a.availability.earliestShipDate)).getTime() - new Date(dateStringReformat(b.availability.earliestShipDate)).getTime()));
                    break;
                default:
                    setSortedData(productSkus.slice(0).sort((a, b) => Number(b.prices?.[0]?.value) - Number(a.prices?.[0]?.value)));
            }
        }
    }, [productSkus]);

    useEffect(() => {
        const index = sortedData.findIndex((item) => item.id === plaSku);
        setSelectedSku(plaSku && index >= 0 ? sortedData[index] : sortedData[0]);
        if (isVaseTemplate) {
            const getCode = selectedSku?.id[11];
            renderVase('', '', getCode, vaseList, productSkus, commonStates, () => {}, plaSku);
        }
    }, [sortedData]);

    // Data from reducer
    const featureFlags = useSelector(getFeatureFlags);
    const addressSource = useSelector(getAddressSource);

    // Feature Flags
    const isOurStoryEnabled = featureFlags['is-pdp-our-story-enabled'];
    const isRecentlyViewedEnabled = featureFlags['is-pdp-recently-viewed-enabled'];
    const isBlogSectionEnabled = featureFlags['is-pdp-blog-section-enabled'];
    const isShippingDisclaimerEnabled = featureFlags['is-shipping-disclaimer-displayed'];

    // Useeffect
    useEffect(() => {
        const index = sortedData.findIndex((item) => item.id === plaSku);
        setSelectedSku(plaSku && index >= 0 ? product.productSkus[index] : product.productSkus[0]);
        setHasAddons(product?.addonGroups?.length > 0);
        setSubscriptionDetails({
            subscriptionType: '',
            subscriptionDuration: '',
            subscriptionFrequency: '',
            subscriptionDurationKey: '',
        });
        setMobileAgeVerificationOpen(false);
        setBaseProduct(null);
    }, [product]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const APP_YOTPO_CLIENT_ID = mbpUtil.getEnv('APP_YOTPO_CLIENT_ID');
            const yotpoClientURL = `https://staticw2.yotpo.com/${APP_YOTPO_CLIENT_ID}/widget.js`;
            addLibraries(yotpoClientURL, '', false, true);
            if (typeof window !== 'undefined' && typeof window.yotpo !== 'undefined' && typeof window.yotpo.refreshWidgets === 'function') {
                try {
                    // Call refresh only if yotpo initialized
                    if (window.yotpo.initialized) {
                        window.yotpo.refreshWidgets();
                    }
                } catch (exception) {
                    mbpLogger.logError({ appName: process.env.npm_package_name, message: 'Unable to refresh the yotpo widget', jsError: exception });
                }
            }
        }
    }, []);

    const handleMiniCartModalClick = (setModal) => {
        if (minicartModalOpen !== setModal) {
            setMinicartModalOpen(setModal);
        }
    };
    // Other variables
    const productName = ReactHtmlParser(product.name);
    const productDisplay = content?.entries?.[0]?.product_name_display;
    const isSmartGiftEnabled = () => {
        if (addressSource === 'SCI') return false;
        if (featureFlags?.['is-smart-gift-enabled']) return true;
        return false;
    };
    const smartGiftLetThemKnowSection = {};
    const setProductQuantity = (qty = 1) => setQuantity(qty);
    const skuConfig = {};
    const deliveryData = {};
    const passportMemberImg = '';
    const productUrl = '';
    let personalization;
    if (productSkus.length) {
        productSkus.forEach((sku) => {
            personalization = sku.personalization;
        });
    }
    const productDeliveryType = product && product.availability && product.availability.productDeliveryType ? product.availability.productDeliveryType : '';
    const language = getLanguageName();

    const graphqlPlantsPDPDataWrapperProps = {
        product,
        hasWine,
        setProductQuantity,
        quantity,
        updateWineStatus: () => {},
        isAddToCartEnabled,
        skuConfig,
        deliveryData,
        presentation_family,
        selectedSku,
        handleSkuChange: (e) => handleSkuChange(e, setSelectedSku, setHasWine, setIsAddToCartEnabled, sortedData),
        sortedData,
        passportMemberImg,
        productUrl,
        subscriptionDetails,
        subscription,
        triggerSubscriptionCheck: (e) => triggerSubscriptionCheck(e, setSubscription),
        location,
        brand,
        isPassportBundleItem,
        handlePersonalizeInputChange: () => handlePersonalizeInputChange(setDisableAddToCart),
        disableAddToCart,
        setDisableAddToCart,
        getUpdatedPersonalizedProdData: () => getUpdatedPersonalizedProdData(setPersonalizedData, personalizedData),
        personalizedData,
        personalization,
        hasAddons,
        baseProductSku: baseProduct,
        isPassportBundleProduct: isPassportBundleProduct || {},
        mobileAgeVerificationOpen,
        handleMobileAgeVerification: () => handleMobileAgeVerification(setMobileAgeVerificationOpen, setAddToCartData, addToCartData),
        addToCartData,
        content: productBaseContent,
        selectedState,
        setSelectedState,
        setAltertType,
        alertType,
        addToCartClicked,
        minicartModalOpen,
        setMinicartModalOpen,
        handleMiniCartModalClick,
        setAddToCartClicked,
        deliveryDate,
        setDeliveryDate,
        cartError,
        variantVaseList,
        renderVase,
        commonStates,
        isVaseTemplate,
        isSmallScreen,
        toastError,
        setToastError,
        vaseList,
        shortDescription: product?.shortDescription,
    };
    /* eslint-disable react/jsx-props-no-spreading */
    /**
     * @description Render the mini cart
     */
    const getMiniCart = () => (
        <>
            {(minicartModalOpen && showLoadingMessage)
                && (
                    <SiteLoader />
                )}

            {
                minicartModalOpen
                && (
                    <MiniCart
                        handleMiniCartModalClick={handleMiniCartModalClick}
                        minicartModalOpen={minicartModalOpen}
                        product={product}
                        presentation_family={presentation_family}
                        isPassportBundleProduct={isPassportBundleProduct || {}}
                    />
                )
            }

        </>
    );
    const goBackButton = () => history.push({ pathname: (location?.state?.prevUrl || '/') });

    const showAggregatedRating = () => (featureFlags['is-show-product-reviews-aggregated-star-rating'] ? 'gold-stars' : 'pr-Review-Image-Display-Disclaimer pr-reviewdisplay-no-aggregated-star-rating black-stars');
    const showRatingDistribution = () => (featureFlags['is-show-product-reviews-star-distribution'] ? '' : classes.hidePRStarDistribution);

    // Track Product
    track(trackProductData({ product }));
    const PRODUCT_QUERY = findCategoryPageByUrl(brand.domain, '/you-may-also-like', 'standard_collections', null, null, false, 'USD', null);
    const { data } = useQuery(PRODUCT_QUERY);
    const { products = [] } = data?.findCategoryPageByUrl?.category || {};
    const youtMayAlsoProducts = products.splice(4);
    const isQubitEnabled = featureFlags['is-qubit-pdp-recommendations-enabled'];
    const isYouMayAlsoLikeEnabled = featureFlags['is-pdp-product-recommendations-enabled'];

    // Product reviews
    const isPdpReviewsEnabled = featureFlags['is-pdp-reviews-enabled'];
    const isPowerReviewEnabled = featureFlags['is-power-review-enabled'];
    return (
        <div className={classes.flowerRoot}>
            {(toastError && alertType === 'nonDeliverable') && <CustomizedSnackbar alertType={alertType} severity="error" message={ErrorMessage[alertType]} setToastError={setToastError} />}
            {(toastError && alertType === 'nonDeliverableOther') && <ShopNotAvailableModal message={ErrorMessage[alertType]} restrictedStates={restrictedStates} />}
            {addOgDynamicData(product, seoData, brand, location)}
            {getMiniCart()}
            <GraphqlProductSeoSchema
                name={product.name}
                shortDescription={product?.shortDescription}
                partNumber={product?.partNumber}
                productSkus={productSkus}
                location={location}
                brand={brand}
                reviews={product?.reviews}
                currency="USD"
            />
            <div className={classes.contentTop}>
                <div className={classes.contentLeft}>
                    {/**
                        * TODO:
                        * - pull correct mobile vs tab breakpoint from cms
                        * - add smart gift to mobile ui
                        */}
                    <DesktopMobile
                        mobile={() => (
                            <>
                                <ShippingInformation isSmallScreen={isSmallScreen} />
                                <MobileHeader
                                    productName={productName}
                                    goBackButton={goBackButton}
                                />
                            </>
                        )}
                        desktop={() => (<></>)}
                        breakpoint={768}
                    />
                    <DesktopMobile
                        mobile={() => (
                            <GraphqlProductImageUIMobile
                                productName={productName}
                                product={product}
                                content={content}
                                selectedSku={selectedSku}
                                presentation_family={presentation_family}
                                productNameDisplay={productDisplay}
                                brand={brand}
                            />
                        )}
                        desktop={() => (
                            <GraphqlProductImageUI
                                data={product}
                                selectedSku={selectedSku}
                                location={location}
                                presentation_family={presentation_family}
                                isSmartGiftEnabled={isSmartGiftEnabled()}
                                smartGiftLetThemKnowSection={smartGiftLetThemKnowSection}
                                brand={brand}
                            />
                        )}
                        breakpoint={1280}
                    />
                </div>
                <div className={`${classes.contentRight} ${isVaseTemplate ? classes.vaseTemplateWrapper : null}`}>
                    <GraphqlPlantsPDPDataWrapper {...graphqlPlantsPDPDataWrapperProps} />
                    {(featureFlags['is-design-and-delivery-popup-enabled']) && (
                        <div>
                            <PDPDeliveryPopupContainer
                                productDeliveryType={productDeliveryType}
                                brand={brand}
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className={classes.contentBottom}>
                {selectedSku && (
                    <GraphqlProductDetailsTabs
                        brand={brand}
                        language={language}
                        partNumber={product?.partNumber}
                        longDescription={product?.longDescription}
                        customClasses={classes}
                        dataDictionary=""
                        pdpDictionary={pdpDictionary}
                        careData={plantsCareData(selectedSku)}
                    />
                )}
                <DesktopMobile
                    mobile={() => (
                        <>
                            <ProductPartNumber classes={classes.partNumberMobileStyle} selectedSku={selectedSku} isMobile />
                            <ProductDivider />
                        </>
                    )}
                    desktop={() => (<></>)}
                    breakpoint={768}
                />
                {isQubitEnabled && content?.entries[0]?.recommendations && (
                    <RecommendationsBuilder
                        partNumber={product?.partNumber}
                        productName={product.name}
                        productCategories={product.parentCategories}
                        recommendations={content?.entries[0]?.recommendations}
                    />
                )}
                {/* Show CMS You May Also like products only if is-qubit-pdp-recommendations-enabled is enabled and
                has product */}
                {(typeof window !== 'undefined' && !isQubitEnabled && isYouMayAlsoLikeEnabled && youtMayAlsoProducts
                && (
                    <>
                        <ProductCarouselBuilder
                            blockData={
                                {
                                    title: 'YOU MAY ALSO LIKE',
                                    title_font_color: '',
                                    title_font_size: '',
                                    title_font_style: '',
                                    title_font_family: '',
                                    product_to_show: '',
                                    wcs_style: false,
                                }
                            }
                            products={products}
                            nDisplayProducts={3}
                            eventTrackingData={{
                                // callbackData,
                            }}
                        />
                        <ProductDivider />
                    </>
                ))}

                {isOurStoryEnabled && (<ProductOurStory />)}
                {typeof window !== 'undefined' && isPdpReviewsEnabled && isPowerReviewEnabled && (
                    <div className={classes.customerReviews}>
                        <ProductTitle title="CUSTOMER REVIEWS" />
                        <div style={{ margin: '0 10px' }}>
                            <div
                                id="pr-reviewdisplay"
                                className={`${classes.prReviewDisplay} pr-Review-Display-Snapshot-Mobile ${showAggregatedRating()} ${showRatingDistribution()}`}
                            />
                            <div
                                id="pr-questiondisplay"
                                className={classes.prQuestionDisplay}
                            />
                        </div>
                        <ProductDivider />
                    </div>
                )}
                {typeof window !== 'undefined' && isRecentlyViewedEnabled && (<ProductRecentlyViewed product={product} />)}
                {isBlogSectionEnabled && (<ProductBlogSection />)}
                <div className={`${classes.breadCrumbBody} ${classes.breadCrumbBody}--desktop`}> {getBreadcrumbArray(product, location, [])}</div>
                {isShippingDisclaimerEnabled && <ShippingDisclaimer />}
            </div>
        </div>
    );
};

GraphqlPlantsProductPageBuilder.defaultProps = {
    isPassportBundleProduct: {},
    plaSku: null,
    restrictedStates: {},
};

GraphqlPlantsProductPageBuilder.propTypes = {
    brandId: string.isRequired,
    product: object.isRequired,
    location: object.isRequired,
    presentation_family: string.isRequired,
    data: object.isRequired,
    classes: object.isRequired,
    content: object.isRequired,
    productBaseContent: object.isRequired,
    isPassportBundleItem: bool.isRequired,
    isPassportBundleProduct: shape({
        prices: {
            value: number,
        },
        enable: bool,
    }),
    cartError: string.isRequired,
    seoData: shape({
        seo_searchable: bool,
        page_title: string,
        keywords: string,
        meta_description: string,
        og: array,
    }).isRequired,
    history: object.isRequired,
    plaSku: string,
    restrictedStates: object,
};

const enhance = compose(
    trackPage(),
    withRouter,
    withApollo,
    withStyles(styles),
    // connect(null, null),
);

export default enhance(GraphqlPlantsProductPageBuilder);
